/**
 * A screen's code that could not be downloaded -- almost always because the app was redeployed while the tab
 * was open.
 *
 * Every page is a `lazy(() => import(...))` chunk with a content hash in its name. A deploy replaces the whole
 * `assets/` directory, so a tab still running the previous build asks for a chunk name that no longer exists,
 * gets 404, and the import rejects. Reloading fetches the current `index.html` (served `no-store`) and with it
 * the current chunk names, which is the whole cure; retrying the same URL is not, since the file is gone.
 *
 * Each browser words the rejection differently, and Vite adds its own for a stylesheet the chunk needs.
 */
const CHUNK_LOAD_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_MESSAGE.test(error.message);
}

const RELOADED_AT_KEY = 'chunkReloadAt';

/** A second failure inside this window means the reload did not help, so the user is asked instead. */
const RELOAD_WINDOW_MS = 30_000;

/**
 * Whether a chunk failure may reload the page by itself -- at most once per {@link RELOAD_WINDOW_MS}, so a
 * chunk that is still missing after the reload (a broken deploy, an outage) cannot loop. The time of the
 * reload is noted in sessionStorage, which survives the reload and is per tab. Where storage is unavailable
 * the answer is no: without the note there is no way to stop a loop.
 */
export function claimChunkReload(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY));
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOADED_AT_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/*
  Module scope: StrictMode renders the error screen twice, and a second claim would be refused and show the
  error while the reload is already on its way. The page is replaced by the reload, so it never needs resetting.
*/
let reloadRequested = false;

/** Whether this error should reload the page -- a chunk failure, with the reload claimed (or already claimed). */
export function shouldReloadForChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  if (!reloadRequested && claimChunkReload()) reloadRequested = true;
  return reloadRequested;
}

export function resetChunkReloadForTests(): void {
  reloadRequested = false;
}
